import type {Fetcher} from './fetcher.ts';

const CACHE_PREFIX = 'hub-fetcher:';

/**
 * Decorator for a Fetcher that uses the Cache Web API
 *
 * Only GET and HEAD requests are cached.
 */
export class WebApiCacheFetcher implements Fetcher {
  fetcher: Fetcher;
  cache: Cache | null;

  constructor(fetcher: Fetcher) {
    this.fetcher = fetcher;
    this.cache = null;

    // delete out-of-date caches (no need to await on it)
    this.deleteOutOfDateCaches();
  }

  async deleteOutOfDateCaches() {
    if (!window.caches) {
      return;
    }

    const currentCacheKey = this.getCurrentCacheKey();
    const existingCacheKeys = await window.caches.keys();
    const deletionPromises = existingCacheKeys.filter((key) => key.startsWith(CACHE_PREFIX) && key !== currentCacheKey).map((key) => {
      console.debug(`Deleting out-of-date cache: ${key}`);
      return window.caches.delete(key);
    });
    await Promise.all(deletionPromises);
  }

  async fetch(requestOrUrl: RequestInfo, options: RequestInit = undefined): Promise<Response> {
    // We're conservative in that we only cache GET and HEAD requests.
    // See https://developer.mozilla.org/en-US/docs/Glossary/cacheable
    const method = (options?.method ?? 'GET').toUpperCase();
    if (method !== 'GET' && method !== 'HEAD') {
      return this.fetcher.fetch(requestOrUrl, options);
    }

    if (window.caches && !this.cache) {
      this.cache = await window.caches.open(this.getCurrentCacheKey());
    }

    const cachedResponse = await this.cache?.match(requestOrUrl);
    if (cachedResponse) {
      return cachedResponse;
    }

    // `fetch` consumes the request, so we need to clone it, since we'll need the request for `cache.put` later
    const response = await this.fetcher.fetch(requestOrUrl instanceof Request ? requestOrUrl.clone() : requestOrUrl, options);

    // NOTE: the other caching fetcher _always_ puts the response in the cache so that it's available to other fetches
    // and then evicts it afterward if it's an error. I think that's overcomplicated and so I'm trying it the naive way
    // but we shall see
    if ((response.status >= 200 && response.status <= 299) || response.status === 304) {
      // `put` consumes the response, so we need to clone it so we can return the original response later
      await this.cache?.put(requestOrUrl, response.clone());
    }

    return response;
  }

  getCurrentCacheKey(): string {
    const hubVersion = window?.config?.renderVersion ? window.config.renderVersion.toString() : 'unknown-version';
    const cacheKey = CACHE_PREFIX + hubVersion;
    return cacheKey;
  }
}
